import { useQuery } from "@tanstack/react-query";
import { articleShareService } from "@/services/articleShareService";
import { Card, CardContent } from "@/components/ui/card";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";
import ArticleCard from "@/components/ui/ArticleCard";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import T from "@/i18n/T";

const SharedWithYouFeed = () => {
  const { data: shares, isLoading } = useQuery({
    queryKey: ['shared-articles'],
    queryFn: () => articleShareService.getSharedWithMe(),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-32 mb-2" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
              <Skeleton className="h-40 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!shares || shares.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground"><T k="auto.no-articles-shared-with-you-yet-b032" /></p>
        <p className="text-sm text-muted-foreground mt-2">
          <T k="auto.when-people-share-articles-with-you-they-a0de" />
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {shares.map((share) => {
        const sender = share.sender;
        const article = share.articles;
        
        if (!article) return null;

        return (
          <Card key={share.id} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <ProfileAvatar
                  userId={sender?.id}
                  avatarUrl={sender?.avatar_url}
                  name={sender?.full_name || sender?.username}
                  background={sender?.avatar_background}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">
                    {sender?.full_name || sender?.username || 'Unknown User'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    shared {formatDistanceToNow(new Date(share.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
              
              {share.message && (
                <div className="mb-4 p-3 bg-muted/50 rounded-lg border-l-2 border-primary">
                  <p className="text-sm italic">"{share.message}"</p>
                </div>
              )}
              
              <ArticleCard article={article} variant="row" />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default SharedWithYouFeed;
